import { memo } from 'react';

/**
 * WEAPON ICONS - the Armoury's glyph set.
 *
 * The rack used emoji: ⚔ for melee, 🎯 for ranged, 📋 for the clipboard.
 * Emoji render at the font's whim - different metrics, different colour,
 * different optical weight from everything around them, and they cannot
 * take `currentColor`, so a selected plate could not tint its own icon.
 *
 * A THIRD catalogue, deliberately. `COND_ICONS` (engine/conditions.ts) is
 * conditions and `MAP_ICONS` (MapIcon.jsx) says in its own docblock that it
 * is map furniture. Weapons are neither, and MapIcon's comment about not
 * writing a second convention is about the DRAWING convention, which this
 * follows exactly: a 16×16 grid, stroked unless a shape reads better
 * solid, one stroke weight, `currentColor` throughout.
 */
const WEAPON_ICONS = {
  // A blade: guard, grip, and a point that reads at 14px.
  melee: [{ d: 'M12.5,2.5 L6.5,8.5 M4,11 L2.5,13.5 L5,12 Z' }, { d: 'M4,11 L6.5,8.5' }, { d: 'M8.5,6.5 L11,9' }],

  // A barrel with its muzzle flash lines - a gun, not a target reticle.
  // (MAP_ICONS.target is the reticle, and means "the thing you are
  // shooting AT". The distinction matters on a panel showing both.)
  ranged: [
    { d: 'M2.5,7 L10,7 L10,9.5 L2.5,9.5 Z' },
    { d: 'M4.5,9.5 L4.5,12.5' },
    { d: 'M11.5,8.25 L14,8.25 M11.5,6 L13.5,5 M11.5,10.5 L13.5,11.5' },
  ],

  // A box magazine, seen edge-on with its follower.
  magazine: [{ d: 'M5,3.5 L11,3.5 L11,13 L5,13 Z' }, { d: 'M5,6 L11,6' }],

  // The reload arrow, matching MAP_ICONS.reset's direction.
  reload: [{ d: 'M13,8 A5,5 0 1,1 11,4.2' }, { d: 'M11,1.5 L11,4.5 L8,4.5' }],

  // Righteous Fury: a flame.
  fury: [{ d: 'M8,2 C10.5,5 11.5,6.5 11.5,9 A3.5,3.5 0 1,1 4.5,9 C4.5,6.5 5.5,5 8,2 Z' }],

  // Psychic rating: a warp eye.
  psy: [
    { d: 'M1.5,8 C4,4.5 12,4.5 14.5,8 C12,11.5 4,11.5 1.5,8 Z' },
    { d: 'M8,6.5 A1.5,1.5 0 1,1 8,9.5 A1.5,1.5 0 1,1 8,6.5 Z', fill: true },
  ],

  // Arm a weapon: a plus, drawn at the same weight as everything else
  // rather than borrowed from the font.
  arm: [{ d: 'M8,3.5 L8,12.5 M3.5,8 L12.5,8' }],
};

function WeaponIconImpl({ name, size = 15 }) {
  const paths = WEAPON_ICONS[name];
  if (!paths) return null;
  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block', flexShrink: 0 }}
    >
      {paths.map((path, i) => (
        <path
          key={i}
          d={path.d}
          fill={path.fill ? 'currentColor' : 'none'}
          stroke={path.fill ? 'none' : 'currentColor'}
          strokeWidth={path.fill ? 0 : 1.4}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}

export default memo(WeaponIconImpl);
